import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Typography } from '@components/Typography';
import { Input } from '@components/Input';
import Button from '@components/Button';
import { ExternalLink } from '@components/Link';
import { ModalConfirm } from '@components/Modal';
import { useTranslation, useThrottle, useCopyToClipboard } from '@hooks';
import { mcpConnectionsService } from '@services';
import { alertActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { getHumanizedDate, isUnsetDate } from '@helpers/dates';
import { ExternalRoutes } from '@constants/routes';

const Subtitle = styled(Typography)({
    color: 'var(--text-secondary)',
    marginBottom: '16px',
    maxWidth: 698,
});

const Card = styled('div')({
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 8,
    maxWidth: 698,
    padding: '14px 18px',
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
});

const CardBody = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
});

const Caption = styled(Typography)({
    color: 'var(--text-secondary)',
});

const TitleRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
});

const VerifiedBadge = styled(VerifiedIcon)({
    fontSize: 16,
    color: 'var(--primary-main)',
});

const HowToWrapper = styled('div')({
    maxWidth: 698,
    marginTop: 12,
    marginBottom: 24,
});

const HowToInput = styled(Input)({
    marginTop: 8,
});

const EmptyState = styled('div')({
    maxWidth: 698,
    padding: '4px 0 4px',
});

const McpConnections = ({ accountId }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const language = useSelector(state => state.auth?.user?.language) || 'en';
    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirm, setConfirm] = useState({ open: false, connection: null });

    const mcpUrl = useMemo(() => {
        if (typeof window === 'undefined') return '';
        return `${window.location.protocol}//${window.location.host}/mcp`;
    }, []);

    const refresh = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const list = await mcpConnectionsService.list(accountId);
            setConnections(Array.isArray(list) ? list : []);
        } catch (error) {
            handleApiError(error, 'mcpConnections.errors.fetchFailed');
        } finally {
            setLoading(false);
        }
    }, [accountId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onRevoke = async () => {
        const target = confirm.connection;
        if (!target) return;
        try {
            await mcpConnectionsService.revoke(accountId, target.id);
            dispatch(alertActions.success(t('mcpConnections.revoke.success', { name: target.displayName })));
            setConfirm({ open: false, connection: null });
            refresh();
        } catch (error) {
            handleApiError(error, 'mcpConnections.errors.revokeFailed');
        }
    };

    return (
        <>
            <Typography variant="h6" color="textPrimary" sx={{ marginBottom: '8px' }}>
                {t('mcpConnections.title')}
            </Typography>
            <Subtitle variant="body2">{t('mcpConnections.subtitle')}</Subtitle>

            {!loading && connections.length === 0 && (
                <EmptyState>
                    <Typography variant="subtitle2">{t('mcpConnections.empty.title')}</Typography>
                    <Caption variant="caption" sx={{ display: 'block', marginTop: '6px' }}>
                        {t('mcpConnections.empty.body', { url: mcpUrl })}
                    </Caption>
                </EmptyState>
            )}

            {connections.map(connection => (
                <Card key={connection.id}>
                    <CardBody>
                        <TitleRow>
                            <Typography variant="subtitle2">{connection.displayName}</Typography>
                            {connection.isVerified && <VerifiedBadge titleAccess={t('mcpConnections.row.verified')} />}
                        </TitleRow>
                        <Caption variant="caption">
                            {!isUnsetDate(connection.authorizedAt)
                                ? t('mcpConnections.row.authorizedBy', {
                                      name: connection.authorizedByName || t('common.unknown'),
                                      when: getHumanizedDate(connection.authorizedAt, language),
                                  })
                                : t('mcpConnections.row.authorizedByNoDate', {
                                      name: connection.authorizedByName || t('common.unknown'),
                                  })}
                        </Caption>
                        <Caption variant="caption">
                            {!isUnsetDate(connection.lastUsedAt)
                                ? t('mcpConnections.row.lastUsed', {
                                      when: getHumanizedDate(connection.lastUsedAt, language),
                                  })
                                : t('mcpConnections.row.lastUsedNever')}
                        </Caption>
                    </CardBody>
                    <Button variant="outlined" onClick={() => setConfirm({ open: true, connection })}>
                        {t('buttons.revoke')}
                    </Button>
                </Card>
            ))}

            <HowToWrapper>
                <Typography variant="subtitle2">{t('mcpConnections.howTo.title')}</Typography>
                <Caption variant="caption" sx={{ display: 'block', marginTop: '4px' }}>
                    {t('mcpConnections.howTo.body')}
                </Caption>
                <HowToInput
                    label={t('common.url')}
                    inputProps={{ value: mcpUrl, onChange: () => {} }}
                    onAddonClick={() => onCopyClick(mcpUrl)}
                    addonComponent={<ContentCopyIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />}
                    fullWidth
                    disabled
                />
                <ExternalLink
                    href={ExternalRoutes.LearnMore[i18n.language].mcp}
                    variant="caption"
                    underline="hover"
                    sx={{ display: 'inline-block', marginTop: '8px' }}
                >
                    {t('mcpConnections.howTo.guide')}
                </ExternalLink>
            </HowToWrapper>

            {confirm.open && (
                <ModalConfirm
                    isOpen={confirm.open}
                    handleClose={() => setConfirm({ open: false, connection: null })}
                    title={t('mcpConnections.revoke.confirmTitle')}
                    description={t('mcpConnections.revoke.confirmBody', {
                        name: confirm.connection?.displayName,
                    })}
                    onPrimaryAction={onRevoke}
                    primaryActionText={t('buttons.revoke')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default McpConnections;
